import { min } from 'lodash';
import {
  CLIENT_TYPE,
  PriceRuleSource,
  PriceRuleOffsetType,
} from '@upstart/harmony/lib/constants';
import { Condition } from '@upstart/harmony/lib/legos/constants';
import {
  PriceRulesRevision,
  CarPriceRule,
  PriceRulesEntry,
} from '@upstart/harmony/lib/legos/dealerTypes';
import { UserForPriceMatrix } from './types';
import { MAX_VALUE_FOR_MONETARY_INPUT } from './constants';

export const limitMonetaryInput = (n: number) =>
  min([MAX_VALUE_FOR_MONETARY_INPUT, n]);

/**
 * Resolves the update info for a pricing rule of used vehicles
 */
export function getRuleHistoryEventUpdateInfoForUsed({
  clientType,
  condition,
  users,
  priceRules,
  priceRulesRevisions,
  isDefaultCarRule,
  index,
}: {
  clientType: CLIENT_TYPE;
  condition: Condition;
  users: UserForPriceMatrix[];
  priceRules?: PriceRulesEntry;
  priceRulesRevisions?: PriceRulesRevision[];
  isDefaultCarRule?: boolean;
  index?: number;
}) {
  let ruleRevision: PriceRulesRevision | undefined;

  if (isDefaultCarRule) {
    ruleRevision = priceRulesRevisions?.find(
      revision => !!revision?.diff?.[clientType]?.[condition]?.defaultRuleDiff,
    );
  } else {
    const existingRule = priceRules?.carRules?.[index as number];

    ruleRevision = priceRulesRevisions?.find(revision =>
      revision?.diff?.[clientType]?.[condition]?.carRulesDiff?.find(
        diff => diff.id === existingRule?.id,
      ),
    );
  }
  // get the final user and display their name and the time of the edit
  const updatingUser =
    ruleRevision && users.find(u => u.id === ruleRevision?.userId);

  let updatedBy;
  if (updatingUser) {
    if (updatingUser.firstName || updatingUser.lastName) {
      updatedBy = `${updatingUser.firstName || ''} ${updatingUser.lastName ||
        ''}`;
    } else if (updatingUser.email || updatingUser.username) {
      updatedBy = updatingUser.email || updatingUser.username;
    }
  }

  return {
    updatedAt: ruleRevision?.createdAt,
    updatedBy,
  };
}

export function getOppositeClientType(clientType: CLIENT_TYPE) {
  switch (clientType) {
    case CLIENT_TYPE.ONLINE:
      return CLIENT_TYPE.IN_STORE;
    case CLIENT_TYPE.IN_STORE:
    default:
      return CLIENT_TYPE.ONLINE;
  }
}

export function getDefaultPriceRulesEntry(): PriceRulesEntry {
  return {
    defaultRule: {
      priceSource: PriceRuleSource.FEED_PRICE,
      offsetType: PriceRuleOffsetType.ADD,
      offsetValue: undefined,
    },
    carRules: [],
  };
}

export const sortCarRulesBasedOnMaxYear = (
  a: CarPriceRule,
  b: CarPriceRule,
) => {
  if (!a.maxYear) {
    return 1;
  }
  if (!b.maxYear) {
    return -1;
  }
  if (a.maxYear === b.maxYear) {
    return 0;
  }
  return a.maxYear < b.maxYear ? 1 : -1;
};
